/**
 * WorkflowVisualizer - Minimalist tool execution display
 *
 * Features:
 * - Compact step list with status indicators
 * - Retro mission control color scheme
 * - Duration display for completed steps
 * - Smooth animations
 */

import { CheckCircle2, XCircle, Loader2, Circle } from 'lucide-react'
import { WorkflowStep, ToolStatus } from '../../types/chat'

interface WorkflowVisualizerProps {
  steps: WorkflowStep[]
}

/**
 * Get icon component based on tool status
 */
function getStatusIcon(status: ToolStatus) {
  switch (status) {
    case ToolStatus.COMPLETE:
      return CheckCircle2
    case ToolStatus.RUNNING:
      return Loader2
    case ToolStatus.ERROR:
      return XCircle
    case ToolStatus.PENDING:
    default:
      return Circle
  }
}

/**
 * Get color based on status - retro theme
 */
function getStatusColor(status: ToolStatus): string {
  switch (status) {
    case ToolStatus.COMPLETE:
      return '#66cccc' // cyan
    case ToolStatus.RUNNING:
      return '#ffcc66' // amber
    case ToolStatus.ERROR:
      return '#ff6666' // red
    case ToolStatus.PENDING:
    default:
      return '#666666' // dim
  }
}

/**
 * Format duration in human-readable format
 */
function formatDuration(durationMs: number | null): string {
  if (!durationMs) return ''
  if (durationMs < 1000) return `${durationMs}ms`
  return `${(durationMs / 1000).toFixed(1)}s`
}

function WorkflowVisualizer({ steps }: WorkflowVisualizerProps) {
  if (!steps || steps.length === 0) {
    return null
  }

  return (
    <div className="mt-3 pt-3 border-t border-retro-border">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-mono uppercase tracking-wider text-retro-dim">
          Workflow
        </span>
        <div className="flex-1 h-px bg-retro-border" />
      </div>

      {/* Steps */}
      <div className="space-y-1">
        {steps.map((step, index) => {
          const StatusIcon = getStatusIcon(step.status)
          const color = getStatusColor(step.status)
          const isRunning = step.status === ToolStatus.RUNNING

          return (
            <div
              key={index}
              className="flex items-center gap-2 py-1 px-2 rounded hover:bg-retro-surface/50 transition-colors"
            >
              {/* Status icon */}
              <StatusIcon
                className={`w-3.5 h-3.5 flex-shrink-0 ${isRunning ? 'animate-spin' : ''}`}
                style={{ color }}
              />

              {/* Tool name */}
              <span className="flex-1 font-mono text-xs text-retro-muted truncate">
                {step.tool}
              </span>

              {/* Duration or status indicator */}
              {step.status === ToolStatus.COMPLETE && step.duration_ms && (
                <span className="text-xs font-mono" style={{ color: '#66cccc' }}>
                  {formatDuration(step.duration_ms)}
                </span>
              )}

              {step.status === ToolStatus.ERROR && (
                <span
                  className="text-xs font-mono truncate max-w-[120px]"
                  style={{ color: '#ff6666' }}
                  title={step.error || 'Error'}
                >
                  {step.error || 'failed'}
                </span>
              )}

              {isRunning && (
                <span className="text-xs font-mono animate-pulse" style={{ color: '#ffcc66' }}>
                  running
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default WorkflowVisualizer
